/**
 * Void Contract Dialog Component
 *
 * Allows admin/recruiter to void a contract instance and create a new version.
 * Used when contract needs to be reset (LO needs to sign again).
 *
 * Features:
 * - Warning message about consequences
 * - Required void reason (20-500 chars)
 * - Version count display
 * - Confirmation checkbox
 * - Calls voidAndCreateNewVersion service
 */

import React, { useState } from 'react';
import { LwModal } from '@/components/lw';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { toast } from 'sonner';
import type { ContractInstanceData } from '@/types/recruitment';
import {
  voidAndCreateNewVersion,
  validateVoidReason,
  getVersionCount
} from '@/services/contractVersionService';
import { useAuth } from '@/contexts/useAuth';

interface VoidContractDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractInstance: ContractInstanceData;
  onVoidSuccess: () => void;
}

const MIN_REASON_LENGTH = 20;
const MAX_REASON_LENGTH = 500;

export const VoidContractDialog: React.FC<VoidContractDialogProps> = ({
  open,
  onOpenChange,
  contractInstance,
  onVoidSuccess
}) => {
  const { user } = useAuth();
  const [voidReason, setVoidReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [versionCount, setVersionCount] = useState<number | null>(null);

  // Load version count when dialog opens
  React.useEffect(() => {
    if (open && contractInstance.recruitId) {
      getVersionCount(contractInstance.recruitId).then(counts => {
        setVersionCount(counts.total);
      });
    }
  }, [open, contractInstance.recruitId]);

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!open) {
      setVoidReason('');
      setConfirmed(false);
      setVersionCount(null);
    }
  }, [open]);

  const handleVoid = async () => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    // Validate reason
    const validation = validateVoidReason(voidReason);
    if (!validation.allowed) {
      toast.error(validation.reason || 'Invalid void reason');
      return;
    }

    if (!confirmed) {
      toast.error('Please confirm you understand the consequences');
      return;
    }

    setIsVoiding(true);

    try {
      const result = await voidAndCreateNewVersion(
        contractInstance.id,
        user.id,
        voidReason.trim()
      );

      if (result.success) {
        toast.success(
          `Contract voided successfully. New version ${result.versionNumber} created.`
        );
        onOpenChange(false);
        onVoidSuccess();
      } else {
        toast.error(result.error || 'Failed to void contract');
      }
    } catch (error) {
      console.error('Error voiding contract:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsVoiding(false);
    }
  };

  const reasonLength = voidReason.trim().length;
  const isReasonValid = reasonLength >= MIN_REASON_LENGTH && reasonLength <= MAX_REASON_LENGTH;
  const canSubmit = isReasonValid && confirmed && !isVoiding;

  // Calculate remaining characters
  const remainingChars = MAX_REASON_LENGTH - reasonLength;

  if (!open) {
    return null;
  }

  return (
    <LwModal
      eyebrow="Contract"
      title="Void Contract"
      sub="This action will void the current contract version and create a new draft. The loan officer will need to sign again."
      onClose={() => onOpenChange(false)}
      footer={
        <>
          <button className="btn-sm" onClick={() => onOpenChange(false)} disabled={isVoiding} type="button">
            Cancel
          </button>
          <button className="btn-sm danger" onClick={handleVoid} disabled={!canSubmit} type="button">
            {isVoiding ? (
              <>
                <Loader2 className="animate-spin" />
                Voiding Contract…
              </>
            ) : (
              'Void Contract'
            )}
          </button>
        </>
      }
    >
      {/* Version Count Badge */}
      {versionCount !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            className="badge"
            style={
              versionCount >= 4
                ? { background: '#E9DAD3', color: '#7E3B27' }
                : { background: '#EEEADD', color: '#6F6A57' }
            }
          >
            Version {contractInstance.versionNumber || 1} of 5
          </span>
          {versionCount >= 4 && (
            <span style={{ fontSize: 12.5, color: 'var(--lw-muted)' }}>Approaching version limit</span>
          )}
        </div>
      )}

      {/* Warning */}
      <div className="err-box">
        <span className="ic"><AlertTriangle /></span>
        <div>
          <div className="err-title">Voiding this contract will:</div>
          <ul className="err-detail" style={{ listStyle: 'disc', paddingLeft: 18, marginTop: 4 }}>
            <li>Mark the current version as voided (preserves audit trail)</li>
            <li>Create a new draft contract (version {(contractInstance.versionNumber || 1) + 1})</li>
            <li>Clear all signature data</li>
            <li>Reset recruit status to "Inquiry"</li>
            <li>Require loan officer to sign again</li>
          </ul>
        </div>
      </div>

      {/* Void Reason Input */}
      <div className="field">
        <label htmlFor="void-reason">
          Reason for Voiding <span className="req">*</span>
        </label>
        <textarea
          id="void-reason"
          placeholder={`Please provide a detailed reason for voiding this contract (minimum ${MIN_REASON_LENGTH} characters)...\n\nExamples:\n- Incorrect compensation terms were entered\n- Loan officer requested changes before signing\n- Wrong mentor was assigned in contract`}
          value={voidReason}
          onChange={(e) => setVoidReason(e.target.value)}
          rows={6}
          maxLength={MAX_REASON_LENGTH}
          className={reasonLength > 0 && !isReasonValid ? 'invalid' : ''}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, marginTop: 6 }}>
          <div>
            {reasonLength > 0 && (
              <span style={{ color: isReasonValid ? 'var(--green-700)' : 'var(--danger)', fontWeight: 600 }}>
                {reasonLength < MIN_REASON_LENGTH
                  ? `${MIN_REASON_LENGTH - reasonLength} more characters needed`
                  : '✓ Valid length'}
              </span>
            )}
          </div>
          <span style={{ color: remainingChars < 50 ? 'var(--danger)' : 'var(--lw-muted)' }}>
            {remainingChars} characters remaining
          </span>
        </div>
      </div>

      {/* Confirmation Checkbox */}
      <div className="panel" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 14 }}>
        <Checkbox
          id="confirm-void"
          checked={confirmed}
          onCheckedChange={(checked) => setConfirmed(checked as boolean)}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label htmlFor="confirm-void" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer', lineHeight: 1.2 }}>
            I understand the consequences
          </label>
          <p style={{ fontSize: 12, color: 'var(--lw-muted)', margin: 0 }}>
            I confirm that voiding this contract is necessary and that the loan officer will need to review and sign the new version.
          </p>
        </div>
      </div>

      {/* Contract Details */}
      <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 14, fontSize: 12.5, color: 'var(--lw-muted)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <p style={{ margin: 0 }}><strong>Contract ID:</strong> {contractInstance.id}</p>
        <p style={{ margin: 0 }}><strong>Current Status:</strong> {contractInstance.status}</p>
        <p style={{ margin: 0 }}><strong>Created:</strong> {parseAsLocalDate(contractInstance.createdAt).toLocaleString()}</p>
        {contractInstance.loanOfficerSignatureDate && (
          <p style={{ margin: 0 }}><strong>LO Signed:</strong> {parseAsLocalDate(contractInstance.loanOfficerSignatureDate).toLocaleString()}</p>
        )}
      </div>
    </LwModal>
  );
};
